import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HelpCircle, Briefcase, UserCircle, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { LanguageToggle } from "../LanguageToggle";
import { ShareMenu } from "../ShareMenu";
import { BRAND } from "../../config";

export const Navbar = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const NavLink = ({ to, children, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm font-medium transition-colors ${
        isActive(to) ? "text-brand" : "text-stone-sub hover:text-brand"
      }`}
    >
      {children}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-lg text-stone-text hidden sm:block">
            Restaurante<span className="text-brand">Jobs</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/jobs" testid="nav-jobs">{t("nav.browse")}</NavLink>
          <NavLink to="/help" testid="nav-help">{t("nav.help")}</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ShareMenu />
          <LanguageToggle />
          <Link to="/help" data-testid="nav-help-icon" className="p-2 rounded-full hover:bg-brand-soft text-stone-sub hover:text-brand transition-colors">
            <HelpCircle className="w-5 h-5" />
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="flex items-center gap-2 text-sm font-medium text-stone-sub hover:text-brand"
              >
                <UserCircle className="w-5 h-5" /> {user.name?.split(" ")[0]}
              </Link>
              <button data-testid="nav-logout" onClick={doLogout} className="p-2 rounded-full hover:bg-brand-soft text-stone-muted hover:text-brand transition-colors">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-stone-sub hover:text-brand">
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                data-testid="nav-signup"
                className="text-sm font-semibold px-4 py-2 rounded-xl bg-brand text-white hover:bg-brand-hover hover:-translate-y-0.5 transition-all"
              >
                {t("nav.signup")}
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="md:hidden p-2 text-stone-text"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-border bg-white px-4 py-4 flex flex-col gap-4" data-testid="mobile-menu">
          <div className="flex items-center justify-between">
            <LanguageToggle />
            <Link to="/help" onClick={() => setOpen(false)} className="text-sm text-stone-sub">{t("nav.help")}</Link>
          </div>
          <ShareMenu />
          <Link to="/jobs" onClick={() => setOpen(false)} className="text-sm font-medium text-stone-text">{t("nav.browse")}</Link>
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium text-stone-text">{t("nav.dashboard")}</Link>
              <button onClick={() => { doLogout(); setOpen(false); }} className="text-left text-sm font-medium text-brand">{t("nav.logout")}</button>
            </>
          ) : (
            <div className="flex gap-3">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center text-sm font-medium px-4 py-2 rounded-xl border border-stone-border">{t("nav.login")}</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center text-sm font-semibold px-4 py-2 rounded-xl bg-brand text-white">{t("nav.signup")}</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
